import { AgGridWithModules } from "$root/third-party-integrations/ag-grid/ag-grid";
import type { CustomAxiosError } from "$root/third-party-integrations/axios";
import {
	InvestableUniverseSelectionStrategy,
	type UniverseConflictResolution,
	type PartialInvestableUniverseTicker,
	type UniverseCoverageConflict,
} from "@eu.mdotm.api/sphere";
import { Button, Radio, RadioGroup, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import {
	type DialogProps,
	AutoTooltip,
	Collapsible,
	DefaultCollapsibleContent,
	DefaultCollapsibleHeader,
	Dialog,
	DialogFooter,
} from "@mdotm/shadcn/legacy/components";
import { type SpawnResult, adaptAnimatedNodeProvider, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { ForEach } from "@mdotm/utils/react-extra";
import { unreachable } from "@mdotm/utils/runtime";
import type { ColDef } from "ag-grid-community";
import { useState } from "react";

// #region Conflict grouping & retry payload
export type EnhancementAxiosError = CustomAxiosError & {
	conflicts?: UniverseCoverageConflict[];
	problematicUniverses?: { [universeIdentifier: string]: boolean };
};

export type ConflictGroupKey = "editable" | "nonEditable";

/** Strategy chosen by the user for each edit-permission group of conflicting universes. */
export type UniverseConflictStrategyChoices = Partial<Record<ConflictGroupKey, InvestableUniverseSelectionStrategy>>;

type ConflictGroup = {
	key: ConflictGroupKey;
	universeEditable: boolean;
	conflicts: UniverseCoverageConflict[];
};

/**
 * Single source of the edit-permission grouping predicate: both the dialog sections and the
 * retry payload derive their groups from here, so a universe always lands on the choice the
 * user actually made for it.
 */
function groupConflicts(conflicts: UniverseCoverageConflict[]): ConflictGroup[] {
	const editable = conflicts.filter((conflict) => conflict.universeEditable !== false);
	const nonEditable = conflicts.filter((conflict) => conflict.universeEditable === false);
	const groups: ConflictGroup[] = [];
	if (editable.length > 0) {
		groups.push({ key: "editable", universeEditable: true, conflicts: editable });
	}
	if (nonEditable.length > 0) {
		groups.push({ key: "nonEditable", universeEditable: false, conflicts: nonEditable });
	}
	return groups;
}

/**
 * Builds the bulk-enhancement retry payload: one entry per conflicting universe, resolved with
 * the strategy the user picked for its edit-permission group.
 */
export function universeConflictStrategiesPayload(
	conflicts: UniverseCoverageConflict[] | undefined,
	choices: UniverseConflictStrategyChoices,
): UniverseConflictResolution[] {
	const seen = new Set<string>();
	return groupConflicts(conflicts ?? []).flatMap((group) =>
		group.conflicts.flatMap((conflict) => {
			const strategy = choices[group.key];
			const universeIdentifier = conflict.universeIdentifier;
			if (!strategy || !universeIdentifier || seen.has(universeIdentifier)) {
				return [];
			}
			seen.add(universeIdentifier);
			return [{ universeIdentifier, strategy }];
		}),
	);
}
// #endregion

// #region Dialog variant resolution
export type UniverseConflictDialogFlow = "single" | "bulk";

type ConflictDialogVariant =
	| {
			/** Single-portfolio flow (or no conflict details): backend message + today's flat layout. */
			kind: "single";
			conflicts: UniverseCoverageConflict[];
			universeEditable: boolean;
	  }
	| {
			/** Bulk enhancement: one labeled section per edit-permission group, naming investments and universes. */
			kind: "bulk";
			groups: ConflictGroup[];
	  };

function resolveVariant(
	flow: UniverseConflictDialogFlow,
	conflicts: UniverseCoverageConflict[] | undefined,
): ConflictDialogVariant {
	const knownConflicts = conflicts ?? [];
	// Missing conflict details can't be sectioned, so they fall back to the single layout
	// (backend message + a single editable strategy choice) even in the bulk flow.
	if (flow === "single" || knownConflicts.length === 0) {
		return {
			kind: "single",
			conflicts: knownConflicts,
			universeEditable: !knownConflicts.some((conflict) => conflict.universeEditable === false),
		};
	}
	return { kind: "bulk", groups: groupConflicts(knownConflicts) };
}

function requiredChoiceKeys(variant: ConflictDialogVariant): ConflictGroupKey[] {
	switch (variant.kind) {
		case "single": {
			return [variant.universeEditable ? "editable" : "nonEditable"];
		}
		case "bulk": {
			return variant.groups.map((group) => group.key);
		}
		default: {
			unreachable(variant);
		}
	}
}
// #endregion

// #region Conflict presentation building blocks
const conflictInstrumentColumns: ColDef<PartialInvestableUniverseTicker>[] = [
	{ headerName: "Name", field: "instrument", flex: 1 },
	{ headerName: "ISIN", field: "isin", width: 140 },
];

const conflictTableRowHeight = 32;
const conflictTableHeaderHeight = 32;
const conflictTableMaxHeight = 160;

function ConflictInstrumentsTable({
	instruments,
}: {
	instruments: PartialInvestableUniverseTicker[];
}): React.JSX.Element {
	// Normal (non-autoHeight) layout so the grid scrolls rows internally and the header
	// stays pinned; the wrapper height must therefore be definite, sized to the content
	// (+2px so pixel rounding doesn't trigger a spurious scrollbar) and capped.
	const height = Math.min(
		conflictTableHeaderHeight + instruments.length * conflictTableRowHeight + 2,
		conflictTableMaxHeight,
	);
	return (
		<AgGridWithModules<PartialInvestableUniverseTicker>
			containerStyle={{ height }}
			rowHeight={conflictTableRowHeight}
			headerHeight={conflictTableHeaderHeight}
			columnDefs={conflictInstrumentColumns}
			rowData={instruments}
		/>
	);
}

function conflictHeaderLabel(conflict: UniverseCoverageConflict, withUniverseName: boolean): string {
	const investmentName = conflict.investment?.name ?? "Investment";
	if (withUniverseName && conflict.universeName) {
		return `${investmentName} — Universe: ${conflict.universeName}`;
	}
	return investmentName;
}

function ConflictCollapsibleList({
	conflicts,
	withUniverseNames = false,
}: {
	conflicts: UniverseCoverageConflict[];
	withUniverseNames?: boolean;
}): React.JSX.Element {
	return (
		<ForEach
			collection={conflicts}
			keyProvider={(conflict, conflictIndex) => conflict.investment?.uuid ?? conflictIndex}
		>
			{(conflict) => (
				<Collapsible
					unmountOnExit
					header={(headerProps) => (
						<DefaultCollapsibleHeader {...headerProps}>
							{conflictHeaderLabel(conflict, withUniverseNames)}
						</DefaultCollapsibleHeader>
					)}
				>
					{({ expand }) => (
						<DefaultCollapsibleContent expand={expand}>
							<ConflictInstrumentsTable instruments={conflict.instrumentsOutsideUniverse ?? []} />
						</DefaultCollapsibleContent>
					)}
				</Collapsible>
			)}
		</ForEach>
	);
}

function conflictSectionHeadline(investmentCount: number, universeEditable: boolean): string {
	if (!universeEditable) {
		if (investmentCount === 1) {
			return "Sphere has detected that 1 portfolio holds instruments that are not included in a universe you are not authorized to modify. As this universe cannot be expanded, the conflict can be resolved only by selling the instruments held outside it.";
		}
		return `Sphere has detected that ${investmentCount} portfolios hold instruments that are not included in universes you are not authorized to modify. As these universes cannot be expanded, the conflict can be resolved only by selling the instruments held outside them.`;
	}
	if (investmentCount === 1) {
		return "Sphere has detected that 1 portfolio holds instruments that are not included in the selected universe. To ensure accurate portfolio management, all portfolio instruments must be contained within the universe.";
	}
	return `Sphere has detected that ${investmentCount} portfolios hold instruments that are not included in the selected universe. To ensure accurate portfolio management, all portfolio instruments must be contained within the universe.`;
}

function StrategyRadioGroup({
	value,
	onChange,
	universeEditable,
	qualifierBase,
}: {
	value: InvestableUniverseSelectionStrategy | null;
	onChange(strategy: InvestableUniverseSelectionStrategy): void;
	universeEditable: boolean;
	qualifierBase: string;
}): React.JSX.Element {
	const expandRadio = (
		<Radio
			value={InvestableUniverseSelectionStrategy.Expand}
			disabled={!universeEditable}
			data-qualifier={`${qualifierBase}(expand)`}
		>
			Expand selected universe
		</Radio>
	);

	return (
		<RadioGroup value={value} onChange={onChange}>
			<div className="flex flex-row flex-wrap gap-4" data-qualifier={qualifierBase}>
				{universeEditable ? (
					expandRadio
				) : (
					<AutoTooltip
						position="top"
						mode="hover"
						severity="success"
						trigger={({ innerRef }) => <span ref={innerRef}>{expandRadio}</span>}
					>
						You don&apos;t have permission to edit this universe
					</AutoTooltip>
				)}
				{/* <Radio
					value={InvestableUniverseSelectionStrategy.CreateNew}
					data-qualifier="portfolioWizard/universeConflict/dialog/strategy(createNew)"
				>
					Create new universe
				</Radio> */}
				<Radio
					value={InvestableUniverseSelectionStrategy.SellInstruments}
					data-qualifier={`${qualifierBase}(sellInstruments)`}
				>
					Sell instruments
				</Radio>
			</div>
		</RadioGroup>
	);
}

const strategyGroupQualifiers: Record<ConflictGroupKey, string> = {
	editable: "portfolioWizard/universeConflict/dialog/strategyForEditableUniverses",
	nonEditable: "portfolioWizard/universeConflict/dialog/strategyForNonEditableUniverses",
};

function ConflictSection({
	group,
	value,
	onChange,
}: {
	group: ConflictGroup;
	value: InvestableUniverseSelectionStrategy | null;
	onChange(strategy: InvestableUniverseSelectionStrategy): void;
}): React.JSX.Element {
	return (
		<div className="flex flex-col gap-4">
			<div>{conflictSectionHeadline(group.conflicts.length, group.universeEditable)}</div>
			<ConflictCollapsibleList conflicts={group.conflicts} withUniverseNames />
			<StrategyRadioGroup
				value={value}
				onChange={onChange}
				universeEditable={group.universeEditable}
				qualifierBase={strategyGroupQualifiers[group.key]}
			/>
		</div>
	);
}
// #endregion

// #region Dialog & spawner
type UniverseConflictStrategyDialogProps = {
	show: boolean;
	/**
	 * Which flow raised the conflict. The variant can't be inferred from the conflicts alone:
	 * a bulk enhancement with exactly one conflicting portfolio looks identical to the single
	 * flow, but must still name the portfolio and universe it belongs to.
	 */
	flow: UniverseConflictDialogFlow;
	message: string;
	conflicts?: UniverseCoverageConflict[];
	onSubmit(choices: UniverseConflictStrategyChoices): Promise<void>;
	onClose(): void;
	onAnimationStateChange?: DialogProps["onAnimationStateChange"];
};
function UniverseConflictStrategyDialog({
	show,
	flow,
	message,
	conflicts,
	onSubmit,
	onClose,
	onAnimationStateChange,
}: UniverseConflictStrategyDialogProps) {
	const [choices, setChoices] = useState<UniverseConflictStrategyChoices>({});

	const variant = resolveVariant(flow, conflicts);
	const canSubmit = requiredChoiceKeys(variant).every((key) => choices[key] != null);

	function setChoice(key: ConflictGroupKey, strategy: InvestableUniverseSelectionStrategy): void {
		setChoices((current) => ({ ...current, [key]: strategy }));
	}

	function variantBody(): React.JSX.Element {
		switch (variant.kind) {
			case "single": {
				const key: ConflictGroupKey = variant.universeEditable ? "editable" : "nonEditable";
				return (
					<div className="flex flex-col gap-4">
						<div>{message}</div>
						{variant.conflicts.length > 0 &&
							(variant.conflicts.length === 1 ? (
								<ConflictInstrumentsTable instruments={variant.conflicts[0].instrumentsOutsideUniverse ?? []} />
							) : (
								<ConflictCollapsibleList conflicts={variant.conflicts} />
							))}
						<StrategyRadioGroup
							value={choices[key] ?? null}
							onChange={(strategy) => setChoice(key, strategy)}
							universeEditable={variant.universeEditable}
							qualifierBase="portfolioWizard/universeConflict/dialog/strategy"
						/>
					</div>
				);
			}
			case "bulk": {
				return (
					<div className="flex flex-col gap-6">
						{variant.groups.map((group) => (
							<ConflictSection
								key={group.key}
								group={group}
								value={choices[group.key] ?? null}
								onChange={(strategy) => setChoice(group.key, strategy)}
							/>
						))}
					</div>
				);
			}
			default: {
				unreachable(variant);
			}
		}
	}

	return (
		<Dialog
			header="Some instruments are not included the selected universe"
			show={show}
			size="xlarge"
			onSubmitAsync={async () => {
				if (!canSubmit) {
					return;
				}
				await onSubmit(choices);
			}}
			onAnimationStateChange={onAnimationStateChange}
			onClose={onClose}
			footer={() => (
				<DialogFooter
					primaryAction={
						<SubmitButton disabled={!canSubmit} data-qualifier="portfolioWizard/universeConflict/dialog/proceed">
							Proceed
						</SubmitButton>
					}
					neutralAction={
						<Button
							palette="tertiary"
							onClick={onClose}
							data-qualifier="portfolioWizard/universeConflict/dialog/cancel"
						>
							Cancel
						</Button>
					}
				/>
			)}
		>
			{variantBody()}
		</Dialog>
	);
}

type SpawnUniverseConflictStrategyDialogProps = Omit<UniverseConflictStrategyDialogProps, "show" | "onClose">;
export function spawnUniverseConflictStrategyDialog(
	props: SpawnUniverseConflictStrategyDialogProps,
): SpawnResult<void> {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ show, resolve, onHidden }) => (
			<UniverseConflictStrategyDialog
				{...props}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve()}
				onSubmit={async (choices) => {
					await props.onSubmit(choices);
					resolve();
				}}
			/>
		)),
	);
}
// #endregion
